/**
 * Which declaration set does this person make, if any?
 *
 * `null` means EXEMPT: this person makes no declaration and must never be gated. Returning the wrong
 * set here is the worst failure mode in this whole feature, because the result gets written down as
 * evidence of what someone promised. So the rules are explicit and the fallback is "no declaration",
 * never "the applicant one".
 *
 * Exempt, deliberately:
 *   - staff (they are not a client and promise nothing)
 *   - witness / dependant (they complete no sponsored form)
 *   - a sponsor on a visa that HAS NO SPONSOR FORM (461, 020, 600-tourist). Production really does
 *     contain one of these: a sponsor contact sits on a 461 matter. pickFormId returns null for them,
 *     so they are never served a sponsor form and therefore undertake nothing. Gating them would ask
 *     them to promise something the Department never asked of them.
 */

import type { DeclarationAudience } from './declarationSet';

/** Partner-visa subclasses whose sponsor completes a 40SP. */
const PARTNER_SPONSOR_VISAS = new Set(['820', '801', '309', '100', '300']);

/** The 600 family-sponsored stream, whose sponsor completes Form 1149 (the security-bond one). */
const VISITOR_SPONSOR_VISAS = new Set(['600-family']);

export function declarationAudienceFor(
  contactType: string | null | undefined,
  visaSubclass: string | null | undefined,
): DeclarationAudience | null {
  if (contactType === 'applicant') return 'applicant';

  if (contactType === 'sponsor') {
    if (!visaSubclass) return null; // cannot tell what they are sponsoring: promise nothing
    if (VISITOR_SPONSOR_VISAS.has(visaSubclass)) return 'sponsor_visitor';
    if (PARTNER_SPONSOR_VISAS.has(visaSubclass)) return 'sponsor_partner';
    return null; // sponsor on a visa with no sponsor form
  }

  return null;
}
